import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, isArtistProtected, readArtistToken, mintArtistToken, clearArtistToken, verifyArtistMembership, updateArtistDisplayName } from '../lib/supabase';
import { toE164 } from '../lib/phone';
import { Lock, AlertCircle, Loader, MessageCircle, User } from 'lucide-react';

interface ArtistAuthGuardProps {
  children: React.ReactNode;
}

export const ArtistAuthGuard: React.FC<ArtistAuthGuardProps> = ({ children }) => {
  const { id } = useParams<{ id: string }>();
  const [status, setStatus] = useState<'loading' | 'authorized' | 'needs_phone' | 'needs_code' | 'needs_display_name' | 'error' | 'token_error'>('loading');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [artistName, setArtistName] = useState('');
  const [checking, setChecking] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [isProtected, setIsProtected] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const checkAccessRef = useRef(false);

  useEffect(() => {
    checkAccessRef.current = false;
    checkAccess();
  }, [id]);

  const checkAccess = async () => {
    if (!id || checkAccessRef.current) return;
    checkAccessRef.current = true;

    try {
      console.log('[ArtistAuth] Checking access for artist:', id);
      // Check if artist requires verification
      const { data: artist, error } = await (supabase as any)
        .from('artists')
        .select('id, name, must_verify, access_phone, whatsapp_group_id')
        .eq('id', id)
        .single();

      if (error || !artist) {
        console.error('[ArtistAuth] Artist not found:', error?.message ?? 'no data');
        setStatus('error');
        return;
      }

      setArtistName(artist.name);
      console.log('[ArtistAuth] Artist:', artist.name, '| must_verify:', artist.must_verify);

      // Admin bypass: if the user has a valid Supabase auth session, skip phone verification
      const { data: { session: adminSession } } = await supabase.auth.getSession();
      if (adminSession?.user) {
        console.log('[ArtistAuth] Admin user detected — bypassing phone verification');
        setStatus('authorized');
        return;
      }

      // Check if this artist is in the RLS protected list
      const protectedArtist = await isArtistProtected(id);
      setIsProtected(protectedArtist);
      console.log('[ArtistAuth] isProtected:', protectedArtist);

      // For protected artists: require a valid token (minted after phone verification)
      if (protectedArtist) {
        const existingToken = readArtistToken();
        if (existingToken) {
          console.log('[ArtistAuth] Token found — phone:', existingToken.phone, '| expires:', existingToken.expires_at);
          // Verify the phone is still in this artist's WhatsApp group
          const stillMember = await verifyArtistMembership(id, existingToken.phone);
          console.log('[ArtistAuth] Membership check for artist', id, '| phone:', existingToken.phone, '| stillMember:', stillMember);
          if (stillMember) {
            console.log('[ArtistAuth] Authorized — phone is in artist group');
            // Check if display_name is set
            if (!existingToken.display_name) {
              console.log('[ArtistAuth] No display_name set — prompting');
              setStatus('needs_display_name');
              return;
            }
            setStatus('authorized');
            return;
          }
          console.log('[ArtistAuth] Phone not in artist group — need re-verification');
        } else {
          console.log('[ArtistAuth] No token found — need phone verification');
        }

        setStatus('needs_phone');
        return;
      }

      // Non-protected artist: use the original flow
      if (!artist.must_verify) {
        console.log('[ArtistAuth] Non-protected, no verification needed — authorized');
        setStatus('authorized');
        return;
      }

      // Check if already verified (with 30-day expiration)
      const sessionKey = `artist_verified_${id}`;
      const storedData = sessionStorage.getItem(sessionKey);
      if (storedData) {
        try {
          const data = JSON.parse(storedData);
          const now = Date.now();
          const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
          
          if (data.verified === 'true' && (now - data.timestamp) < thirtyDaysMs) {
            setStatus('authorized');
            return;
          } else if ((now - data.timestamp) >= thirtyDaysMs) {
            // Expired, remove it
            sessionStorage.removeItem(sessionKey);
          }
        } catch {
          // Invalid data, remove it
          sessionStorage.removeItem(sessionKey);
        }
      }

      // Needs phone verification
      setStatus('needs_phone');
    } catch {
      setStatus('error');
    }
  };

  // Normalize phone to international format (+CC..., handles DE/UK/US etc.)
  // Shared logic lives in src/lib/phone.ts — mirrors the edge functions so
  // numbers canonicalize identically everywhere.
  function formatPhone(raw: string): string {
    return toE164(raw) ?? raw;
  }

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || !id) return;

    setChecking(true);
    setError('');

    try {
      // Normalize phone to +49... format
      const normalizedPhone = formatPhone(phone.trim());

      // Send verification code via edge function directly
      // (member check is done server-side by mint-artist-token after code entry)
      const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
      setGeneratedCode(verificationCode);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-whatsapp-verification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            phone: normalizedPhone,
            code: verificationCode,
            name: artistName
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send code');
      }

      // Store normalized phone for later use in mintArtistToken
      setPhone(normalizedPhone);
      setStatus('needs_code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !id) return;

    setChecking(true);
    setError('');

    try {
      if (code.trim() === generatedCode) {
        // Store verification with timestamp (30-day expiration)
        const verificationData = {
          verified: 'true',
          timestamp: Date.now(),
          phone: phone.trim()
        };
        sessionStorage.setItem(`artist_verified_${id}`, JSON.stringify(verificationData));

        // For protected artists: mint the JWT token for RLS-scoped access
        if (isProtected) {
          try {
            console.log('[ArtistAuth] Minting token for artist:', id, '| phone:', phone.trim());
            const token = await mintArtistToken(id, phone.trim());
            console.log('[ArtistAuth] Token minted — phone:', token.phone, '| expires:', token.expires_at);
            // Check if display_name is already set
            if (!token.display_name) {
              console.log('[ArtistAuth] No display_name set — prompting for name');
              setStatus('needs_display_name');
              setChecking(false);
              return;
            }
          } catch (mintErr) {
            console.error('[ArtistAuth] Failed to mint artist token:', mintErr);
            setError(mintErr instanceof Error ? mintErr.message : 'Failed to create token.');
            setChecking(false);
            return;
          }
        }

        console.log('[ArtistAuth] Code verified — authorized');
        setStatus('authorized');
      } else {
        setError('Wrong code. Please try again.');
        setCode('');
      }
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleResendCode = async () => {
    setSendingCode(true);
    setError('');

    try {
      const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
      setGeneratedCode(verificationCode);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-whatsapp-verification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            phone: phone.trim(),
            code: verificationCode,
            name: artistName
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send code');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleDisplayNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;

    setChecking(true);
    setError('');

    try {
      await updateArtistDisplayName(displayName.trim());
      console.log('[ArtistAuth] Display name set:', displayName.trim());
      setStatus('authorized');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save display name.');
    } finally {
      setChecking(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#000000]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Checking access...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#000000] p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Artist not found
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            This artist does not exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'needs_phone') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#000000] p-4">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-6">
            <img src="/NEU_PSD_swipeup-marketing_2.png" alt="SwipeUp Marketing" className="h-28 w-auto" />
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-primary-500" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
                Enter your phone number to receive a verification code via WhatsApp.
              </p>
            </div>

            <form onSubmit={handlePhoneSubmit} className="space-y-4">
              <div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+49 123 456 7890"
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
                {error && (
                  <p className="text-red-500 text-sm mt-2">{error}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={!phone.trim() || checking}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center"
              >
                {checking ? <Loader className="animate-spin h-5 w-5 mr-2" /> : null}
                {checking ? 'Checking...' : 'Send code'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'needs_code') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#000000] p-4">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-6">
            <img src="/NEU_PSD_swipeup-marketing_2.png" alt="SwipeUp Marketing" className="h-28 w-auto" />
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Enter code
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
                We sent a 6-digit code to {phone}.
              </p>
            </div>

            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="- - - - - -"
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center text-2xl tracking-[0.5em] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                  maxLength={6}
                />
                {error && (
                  <p className="text-red-500 text-sm mt-2 text-center">{error}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={!code.trim() || checking}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
              >
                {checking ? 'Checking...' : 'Verify'}
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={sendingCode}
                className="w-full py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50 transition-colors"
              >
                {sendingCode ? 'Sending...' : 'Resend code'}
              </button>
              <button
                type="button"
                onClick={() => { setPhone(''); setCode(''); setError(''); setStatus('needs_phone'); }}
                className="w-full py-2 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
              >
                Change number
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'needs_display_name') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#000000] p-4">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-6">
            <img src="/NEU_PSD_swipeup-marketing_2.png" alt="SwipeUp Marketing" className="h-28 w-auto" />
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-primary-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                What should we call you?
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
                Set your display name for the artist dashboard.
              </p>
            </div>

            <form onSubmit={handleDisplayNameSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                  maxLength={50}
                />
                {error && (
                  <p className="text-red-500 text-sm mt-2 text-center">{error}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={!displayName.trim() || checking}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center"
              >
                {checking ? <Loader className="animate-spin h-5 w-5 mr-2" /> : null}
                {checking ? 'Saving...' : 'Continue'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'token_error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#000000] p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Access failed
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => {
              clearArtistToken();
              setStatus('needs_phone');
              setError('');
            }}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            Verify again
          </button>
        </div>
      </div>
    );
  }

  // Authorized
  return <>{children}</>;
};
